import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatApiErrorDetail } from '../lib/utils';
import { ArrowLeft, Lock, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { 
        token, 
        new_password: formData.password 
      });
      setSubmitted(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="reset-password-page">
        <header className="bg-white border-b border-[#E2E8F0] py-4">
          <div className="container-custom">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="DCA Enterprises" className="h-10 w-auto" />
            </Link>
          </div>
        </header>
        <div className="flex-grow flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-4">Invalid Reset Link</h1>
            <p className="text-[#475569] mb-6">This password reset link is invalid or has expired.</p>
            <Link to="/forgot-password" className="text-[#1D4ED8] font-medium hover:underline">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="reset-password-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] py-4">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="DCA Enterprises" className="h-10 w-auto" />
            </Link>
            <Link 
              to="/login" 
              className="text-sm text-[#475569] hover:text-[#1D4ED8] flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>
        </div>
      </header>

      {/* Form */}
      <div className="flex-grow flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white p-8 border border-[#E2E8F0] shadow-sm">
            {!submitted ? (
              <>
                <div className="text-center mb-8">
                  <h1 className="heading-2 text-2xl text-[#0A0F1C] mb-2" data-testid="reset-password-title">
                    Reset Password
                  </h1>
                  <p className="text-[#475569] text-sm">
                    Enter your new password below.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="reset-password-error">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">New Password</label>
                    <div className="relative">
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="form-input pl-10"
                        data-testid="reset-password-input"
                      />
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0A0F1C] mb-2">Confirm Password</label>
                    <div className="relative">
                      <Input
                        type="password"
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                        className="form-input pl-10"
                        data-testid="reset-password-confirm-input"
                      />
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="btn-primary-blue w-full"
                    data-testid="reset-password-submit-button"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center" data-testid="reset-password-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h2 className="heading-3 text-xl text-[#0A0F1C] mb-2">Password Reset!</h2>
                <p className="text-[#475569] text-sm mb-6">
                  Your password has been successfully reset. You can now log in with your new password.
                </p>
                <Link 
                  to="/login"
                  className="btn-primary-blue inline-block"
                >
                  Go to Login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
